import { forwardRef } from 'react';

import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import * as Styled from './colour-token.style';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { capitalizeFirstLetter } from 'lib/strings/strings';

type Props = {
  isActive?: boolean;
  isSmall?: boolean;
  size?: number;
  colours: Styled.ColourItem;
};

type ColourTokenFormRadioProps = Props & {
  name: string;
  defaultValue?: string;
  'data-selector'?: string;
  'aria-describedby'?: string;
};

export const ColourToken = ({ isActive, colours, size }: Props) => (
  <Styled.ColourTokenButtonItem
    $isActive={isActive}
    $colours={colours}
    $size={size}
  >
    <ScreenReaderOnly as="span">
      {colours.name || colours.base}
    </ScreenReaderOnly>
  </Styled.ColourTokenButtonItem>
);

export const ColourTokenButton = ({
  isActive,
  colours,
  size,
  ...rest
}: Props & {
  onClick?: () => void;
}) => (
  <TooltipWrapper hoverText={capitalizeFirstLetter(colours.name)}>
    <Styled.ColourTokenWrapper $size={size}>
      <Styled.ColourTokenButton
        type="button"
        $colours={colours}
        $size={size}
        data-selector={`colour-${colours.name}`}
        {...rest}
      >
        <ColourToken isActive={isActive} colours={colours} size={size} />
      </Styled.ColourTokenButton>
    </Styled.ColourTokenWrapper>
  </TooltipWrapper>
);

export const ColourTokenRadio = ({
  isActive,
  name,
  colours,
  onChange,
  ...rest
}: Props & {
  name: string;
  onChange: () => void;
}) => (
  <TooltipWrapper hoverText={capitalizeFirstLetter(colours.name)}>
    <Styled.ColourTokenRadio>
      <input
        data-selector={colours.name}
        id={colours.base}
        name={name}
        type="radio"
        checked={isActive}
        onChange={onChange}
        {...rest}
      />
      <label htmlFor={colours.base}>
        <Styled.ColourTokenItem $colours={colours} $isActive={isActive}>
          <ScreenReaderOnly as="span">{colours.name}</ScreenReaderOnly>
        </Styled.ColourTokenItem>
      </label>
    </Styled.ColourTokenRadio>
  </TooltipWrapper>
);

export const ColourTokenFormRadio = forwardRef(
  (
    {
      isActive,
      name,
      defaultValue,
      colours,
      ...rest
    }: ColourTokenFormRadioProps,
    ref
  ) => (
    <TooltipWrapper hoverText={capitalizeFirstLetter(colours.name)}>
      <Styled.ColourTokenRadio>
        <input
          data-selector={colours.name}
          id={`${name}-${colours.base}`}
          name={name}
          defaultValue={defaultValue}
          type="radio"
          ref={ref as any}
          {...rest}
        />
        <label htmlFor={`${name}-${colours.base}`}>
          <Styled.ColourTokenItem $colours={colours} $isActive={isActive}>
            <ScreenReaderOnly as="span">{colours.name}</ScreenReaderOnly>
          </Styled.ColourTokenItem>
        </label>
      </Styled.ColourTokenRadio>
    </TooltipWrapper>
  )
);
